import type { ReactNode } from 'react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { cn } from '@/lib/utils'

/**
 * Hairline chip carrying a grade letter or a severity word.
 *
 * The design's chips are NEVER filled: 1px border in the semantic hue at 40%
 * alpha, label in the hue at full strength. Filled chips would need the
 * darkened GRADE_FILL_COLORS to carry white type at 4.5:1; an unfilled chip
 * puts the hue on the card surface instead, where the display values already
 * pass.
 */
export function Chip({
  children,
  color,
  className,
}: {
  children: ReactNode
  /** Any CSS colour, including a var(). */
  color: string
  className?: string
}) {
  return (
    <span
      className={cn(
        'instrument-label inline-flex shrink-0 items-center px-[7px] py-[2px] leading-none',
        className
      )}
      style={{
        color,
        border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
      }}
    >
      {children}
    </span>
  )
}

/**
 * Grade letter chip. Renders nothing when the grade is absent — a tool that
 * withheld its grade must not display a letter it never earned.
 */
export function GradeChip({
  grade,
  className,
}: {
  grade: string | null | undefined
  className?: string
}) {
  if (!grade) return null
  return (
    <Chip
      color={GRADE_COLORS[grade] ?? 'var(--muted-foreground)'}
      className={className}
    >
      {grade}
    </Chip>
  )
}
